import { useEffect, useState } from 'react';
import { AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import { useCreatePatient } from '../../services/httpServices/queries';
import { patientService } from '../../services/httpServices/patientService';
import { splitFullName } from '../../utils/patientName';
import { DateOfBirthInput } from './date-of-birth-input';

interface QuickPatientModalProps {
  /** Whether the dialog is shown. */
  open: boolean;
  /** Close the dialog without creating a patient. */
  onClose: () => void;
  /** Called with the created patient record after a successful save. */
  onCreated: (patient: any) => void;
  /**
   * Optional name/mobile typed into the patient search box. Prefills the quick
   * form so the counter/reception staff do not have to retype it.
   */
  initialQuery?: string;
  /**
   * Optional handler for the "full form" action. When provided, a button is
   * rendered that lets staff switch to the complete patient registration form
   * for cases that need more detail than the quick entry covers.
   */
  onOpenFullForm?: () => void;
}

interface QuickPatientForm {
  fullName: string;
  mobileNumber: string;
  gender: 'male' | 'female' | 'other';
  age: string;
  ageMonths: string;
  dateOfBirth: string;
  emergencyContactName: string;
  emergencyContactRelation: string;
  emergencyContactPhone: string;
}

const EMPTY_FORM: QuickPatientForm = {
  fullName: '',
  mobileNumber: '',
  gender: 'male',
  age: '',
  ageMonths: '',
  dateOfBirth: '',
  emergencyContactName: '',
  emergencyContactRelation: '',
  emergencyContactPhone: '',
};

/** Local `YYYY-MM-DD` for a Date, avoiding the UTC shift of `toISOString`. */
function toLocalIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Estimated date of birth for a patient who only knows their age. With no
 * months, only the birth year is known, so the date is anchored to Jan 1 of
 * that year (e.g. age 35 → 1991-01-01). With months, the date is counted back
 * from today and anchored to the 1st so the month reflects the entered age.
 */
function deriveEstimatedDob(years: number, months: number): string {
  if (months <= 0) return `${new Date().getFullYear() - years}-01-01`;
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - (years * 12 + months));
  return toLocalIsoDate(date);
}

/** Parse a `YYYY-MM-DD…` string as a *local* date (avoids the UTC shift of `new Date(iso)`). */
function parseIsoDateLocal(iso: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return new Date(NaN);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Age in whole months for a given date of birth (YYYY-MM-DD). */
function calculateMonthsFromDob(dob: string): number {
  const today = new Date();
  const birth = parseIsoDateLocal(dob);
  let months =
    (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
  if (today.getDate() < birth.getDate()) months--;
  return Math.max(0, months);
}

/**
 * Minimal patient registration dialog shared by the OPD diagnostic receipt and
 * IPD admission flows. Collects just enough to start a visit (name, mobile,
 * age/gender) and optionally an emergency contact, with a link to the full
 * registration form when more detail is required.
 */
export function QuickPatientModal({
  open,
  onClose,
  onCreated,
  initialQuery = '',
  onOpenFullForm,
}: QuickPatientModalProps) {
  const createPatientMutation = useCreatePatient();
  const [form, setForm] = useState<QuickPatientForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed from the search query each time the dialog opens, and clear any state
  // left over from a previous attempt.
  useEffect(() => {
    if (!open) return;
    const isNumeric = /^\d+$/.test(initialQuery);
    setForm({
      ...EMPTY_FORM,
      fullName: isNumeric ? '' : initialQuery,
      mobileNumber: isNumeric ? initialQuery : '',
    });
    setError(null);
    setSaving(false);
  }, [open, initialQuery]);

  if (!open) return null;

  const handleSave = async () => {
    setError(null);
    const {
      fullName,
      mobileNumber,
      gender,
      age,
      ageMonths,
      dateOfBirth,
      emergencyContactName,
      emergencyContactRelation,
      emergencyContactPhone,
    } = form;
    if (!fullName.trim() || !mobileNumber.trim()) {
      setError('Full name and mobile number are required');
      return;
    }
    const hasYears = age !== '' && !Number.isNaN(Number(age)) && Number(age) >= 0;
    const hasMonths =
      ageMonths !== '' && !Number.isNaN(Number(ageMonths)) && Number(ageMonths) >= 0;
    if (!hasYears && !hasMonths) {
      setError('Age is required');
      return;
    }
    // Age is the required input; derive a date of birth when the exact one is
    // unknown so the record still carries a date for reporting. Years + months
    // are combined so a mixed age like "1 year 2 months" is captured exactly,
    // while the API still receives whole years.
    const yearsValue = hasYears ? Number(age) : 0;
    const monthsValue = hasMonths ? Number(ageMonths) : 0;
    const totalMonths = yearsValue * 12 + monthsValue;
    const ageYears = Math.floor(totalMonths / 12);
    const resolvedDob = dateOfBirth || deriveEstimatedDob(yearsValue, monthsValue);
    const { firstName, middleName, lastName } = splitFullName(fullName);
    setSaving(true);
    try {
      // Only the mobile number decides whether this patient already exists.
      // The name is not unique: the same name with a different mobile number
      // is a different person and must be allowed through.
      const dup = await patientService.checkDuplicate({ mobileNumber });
      if (dup.data && dup.data.length > 0) {
        setError('A patient with this mobile number already exists.');
        setSaving(false);
        return;
      }
      const result = await createPatientMutation.mutateAsync({
        firstName,
        middleName: middleName || undefined,
        lastName,
        mobileNumber,
        gender,
        dateOfBirth: resolvedDob,
        age: ageYears,
        estimatedAge: !dateOfBirth,
        emergencyContactName: emergencyContactName.trim() || undefined,
        emergencyContactRelation: emergencyContactRelation.trim() || undefined,
        emergencyContactPhone: emergencyContactPhone.trim() || undefined,
      });
      if (result.data?.id) {
        onCreated(result.data);
        onClose();
      } else {
        setError('Failed to create patient. Please try again.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to create patient');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-lg space-y-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-text">Quick Patient Entry</h3>
            <p className="text-xs text-text-muted">Just the essentials to start a visit</p>
          </div>
          {onOpenFullForm && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenFullForm();
              }}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-primary hover:bg-primary/10 transition-colors text-xs font-medium"
            >
              <ExternalLink size={13} />
              Full form entry
            </button>
          )}
        </div>
        {error && (
          <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Full Name *</label>
          <input
            type="text"
            value={form.fullName}
            onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
            placeholder="Full name"
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Mobile Number *</label>
          <input
            type="text"
            value={form.mobileNumber}
            onChange={(e) => setForm((p) => ({ ...p, mobileNumber: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">Gender *</label>
            <select
              value={form.gender}
              onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value as any }))}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">Age *</label>
            <div className="flex items-stretch gap-1.5">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                aria-label="Age years"
                title="Years"
                value={form.age}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setForm((p) => {
                    const next = { ...p, age: value };
                    if (value !== '' || p.ageMonths !== '') {
                      next.dateOfBirth = deriveEstimatedDob(
                        value === '' ? 0 : Number(value),
                        p.ageMonths === '' ? 0 : Number(p.ageMonths),
                      );
                    }
                    return next;
                  });
                }}
                placeholder="Yr"
                className="min-w-0 flex-1 px-2 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                aria-label="Age months"
                title="Months"
                maxLength={2}
                value={form.ageMonths}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
                  const value = digits === '' ? '' : String(Math.min(11, Number(digits)));
                  setForm((p) => {
                    const next = { ...p, ageMonths: value };
                    if (value !== '' || p.age !== '') {
                      next.dateOfBirth = deriveEstimatedDob(
                        p.age === '' ? 0 : Number(p.age),
                        value === '' ? 0 : Number(value),
                      );
                    }
                    return next;
                  });
                }}
                placeholder="Mo"
                className="min-w-0 flex-1 px-2 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Date of Birth <span className="font-normal">(optional)</span>
            </label>
            <DateOfBirthInput
              value={form.dateOfBirth}
              onChange={(value) => {
                setForm((p) => {
                  if (!value) return { ...p, dateOfBirth: value };
                  const months = calculateMonthsFromDob(value);
                  const years = Math.floor(months / 12);
                  // Split into whole years + leftover months (e.g. 1 yr 2 mo).
                  return {
                    ...p,
                    dateOfBirth: value,
                    age: String(years),
                    ageMonths: months % 12 === 0 ? '' : String(months % 12),
                  };
                });
              }}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
        </div>
        <div className="h-px bg-border" />
        <p className="text-xs font-medium text-text-muted uppercase">Emergency Contact</p>
        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">Name</label>
          <input
            type="text"
            value={form.emergencyContactName}
            onChange={(e) => setForm((p) => ({ ...p, emergencyContactName: e.target.value }))}
            placeholder="Contact name"
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">Relation</label>
            <input
              type="text"
              value={form.emergencyContactRelation}
              onChange={(e) => setForm((p) => ({ ...p, emergencyContactRelation: e.target.value }))}
              placeholder="e.g. Spouse, Sibling"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">Phone</label>
            <input
              type="text"
              value={form.emergencyContactPhone}
              onChange={(e) => setForm((p) => ({ ...p, emergencyContactPhone: e.target.value }))}
              placeholder="+8801XXXXXXXXX"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Save Patient
          </button>
        </div>
      </div>
    </div>
  );
}
